import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ConsentCategory, ConsentCategoryId, legalApi } from '../../services/api';
import {
  isConsentCurrent,
  markConsentReported,
  readConsent,
  writeConsent,
} from '../../utils/cookieConsent';

/**
 * Çerez bandı + "Çerez Tercihleri" ekranı.
 *
 * Çerez Politikası ve Aydınlatma Metni'nin ikisi de kullanıcıya
 * "internet sitemizde yer alan 'Çerez Tercihleri' butonuna basıp dilediğiniz
 * ayarlamaları yapabilirsiniz" diyor. Bu bileşen o vaadin karşılığı; buton
 * her sayfanın altbilgisinde ve hukuki metin sayfalarının üstünde duruyor.
 *
 * Kategoriler ve hangisinin fiilen kullanıldığı sunucudan geliyor
 * (`/public/cookie-consent`). Uygulamada bugün yalnızca zorunlu ve işlev
 * kayıtları var; analiz/hedefleme kategorileri "kullanılmıyor" olarak
 * gösteriliyor. Kullanılmayan bir kategoriyi normal bir anahtar gibi sunmak,
 * kullanıcıya olmayan bir tercihi varmış gibi göstermek olurdu.
 *
 * ⚠️ Bant, tercih verilene kadar sayfayı KİLİTLEMEZ. Zorunlu çerezler için
 * rıza aranmaz ve uygulamada rızaya bağlı çerez yok; kullanıcıyı içeriğe
 * ulaşmadan önce bir seçime zorlamak (cookie wall) Kurul'un aradığı "özgür
 * irade" ölçütüyle de çelişir.
 */

const OPEN_EVENT = 'dijipo:open-cookie-preferences';

/** Sunucuya ulaşılamazsa bant yine de doğru çalışsın diye asgari kategori seti. */
const FALLBACK: { version: string; categories: ConsentCategory[] } = {
  version: 'fallback',
  categories: [
    {
      id: 'zorunlu',
      label: 'Zorunlu çerezler',
      description: 'Oturum açmanız ve uygulamanın çalışması için gereklidir; kapatılamaz.',
      required: true,
      in_use: true,
    },
    {
      id: 'islev',
      label: 'İşlev çerezleri',
      description: 'Seçtiğiniz öğrenci, yarım kalan ödev taslağı gibi tercihlerinizi hatırlar.',
      required: false,
      in_use: true,
    },
  ],
};

const CookieConsent: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<{ version: string; categories: ConsentCategory[] } | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [selection, setSelection] = useState<ConsentCategoryId[]>([]);

  // Kategori listesi + geçerli sürüm. Başarısız olursa FALLBACK ile devam:
  // bandı hiç göstermemek, sunucu hatasını kullanıcının tercihine dönüştürür.
  useEffect(() => {
    let cancelled = false;
    legalApi
      .cookieConsentConfig()
      .then((res) => {
        if (!cancelled) setConfig(res.data);
      })
      .catch(() => {
        if (!cancelled) setConfig(FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Saklanan tercih yoksa ya da politika sürümü yükseldiyse bandı göster.
  useEffect(() => {
    if (!config) return;
    const stored = readConsent();
    if (!isConsentCurrent(stored, config.version)) {
      setShowBanner(true);
      setSelection(stored?.categories ?? ['zorunlu']);
    } else {
      setSelection(stored!.categories);
    }
  }, [config]);

  // "Çerez Tercihleri" bağlantısı — altbilgiden, hukuki metin sayfasından, her yerden.
  useEffect(() => {
    const open = () => {
      setSelection(readConsent()?.categories ?? ['zorunlu']);
      setShowPrefs(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  // Giriş yapılınca tercihi bir kez sunucuya bildir (KVKK'da ispat yükü bizde).
  // Giriş öncesi verilen tercih yalnızca tarayıcıda durur; kime ait olduğu
  // bilinmediği için sunucuya yazmanın ispat değeri de yok.
  useEffect(() => {
    if (!user) return;
    const stored = readConsent();
    if (!stored || stored.reported) return;
    legalApi
      .recordConsent({
        consent_version: stored.version,
        categories: stored.categories,
        source: 'preferences',
      })
      .then(() => markConsentReported())
      .catch(() => {
        /* bildirilemezse tercih yine geçerli; bir sonraki girişte tekrar denenir */
      });
  }, [user]);

  const save = useCallback(
    (categories: ConsentCategoryId[], source: 'banner' | 'preferences') => {
      const version = config?.version ?? FALLBACK.version;
      const stored = writeConsent(categories, version);
      setSelection(stored.categories);
      setShowBanner(false);
      setShowPrefs(false);
      if (user) {
        legalApi
          .recordConsent({ consent_version: version, categories: stored.categories, source })
          .then(() => markConsentReported())
          .catch(() => {});
      }
    },
    [config, user]
  );

  const optionalCategories = (config?.categories ?? FALLBACK.categories).filter((c) => !c.required);
  const allIds = (config?.categories ?? FALLBACK.categories)
    .filter((c) => c.in_use)
    .map((c) => c.id);

  const toggle = (id: ConsentCategoryId) =>
    setSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (!showBanner && !showPrefs) return null;

  return (
    <>
      {showBanner && !showPrefs && (
        <div
          role="region"
          aria-label="Çerez bilgilendirmesi"
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-[#e6e9ef] bg-white shadow-[0_-8px_30px_rgba(20,30,50,0.10)]"
        >
          <div className="mx-auto flex max-w-[1080px] flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between lg:py-5">
            <p className="text-[13px] leading-[1.65] text-[#4a5260] lg:text-[13.5px]">
              eis ekosis LMS, oturumunuzun açık kalması ve tercihlerinizin hatırlanması için çerez ve
              benzeri teknolojiler kullanır. Reklam veya üçüncü taraf analiz çerezi kullanmıyoruz.
              Ayrıntılar için{' '}
              <Link to="/cerez-politikasi" className="font-semibold text-[#3973bf] hover:underline">
                Çerez Politikası
              </Link>{' '}
              ve{' '}
              <Link to="/aydinlatma-metni" className="font-semibold text-[#3973bf] hover:underline">
                Aydınlatma Metni
              </Link>
              .
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                className="rounded-[12px] border border-[#d5dbe5] px-4 py-2.5 text-[13px] font-semibold text-[#4a5260] transition-colors hover:bg-[#f4f6fa]"
              >
                Çerez Tercihleri
              </button>
              <button
                type="button"
                onClick={() => save(['zorunlu'], 'banner')}
                className="rounded-[12px] border border-[#d5dbe5] px-4 py-2.5 text-[13px] font-semibold text-[#4a5260] transition-colors hover:bg-[#f4f6fa]"
              >
                Yalnızca Zorunlu
              </button>
              <button
                type="button"
                onClick={() => save(allIds, 'banner')}
                className="rounded-[12px] bg-[#3973bf] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Tümünü Kabul Et
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrefs && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-prefs-title"
            className="max-h-[88vh] w-full max-w-[560px] overflow-y-auto rounded-t-[20px] bg-white p-6 shadow-2xl sm:rounded-[20px]"
          >
            <h2 id="cookie-prefs-title" className="text-[18px] font-bold text-[#2b2b2b]">
              Çerez Tercihleri
            </h2>
            <p className="mt-2 text-[13px] leading-[1.7] text-[#5b6472]">
              Hangi kayıtların tutulacağını buradan seçebilirsiniz. Uygulamada tutulan çerezlerin ve
              yerel kayıtların tam listesi için{' '}
              <Link
                to="/cerez-listesi"
                onClick={() => setShowPrefs(false)}
                className="font-semibold text-[#3973bf] hover:underline"
              >
                Çerez Listesi
              </Link>
              'ne bakabilirsiniz.
            </p>

            <div className="mt-5 space-y-3">
              {/* Zorunlu kategori ayrı çiziliyor: kapatılamayan bir anahtar
                  göstermek yerine neden kapatılamadığını yazmak daha dürüst. */}
              {(config?.categories ?? FALLBACK.categories)
                .filter((c) => c.required)
                .map((cat) => (
                  <div key={cat.id} className="rounded-[14px] border border-[#e6e9ef] bg-[#f7f9fc] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-[14px] font-semibold text-[#2b2b2b]">{cat.label}</span>
                      <span className="shrink-0 rounded-full bg-[#e8f0fb] px-2.5 py-1 text-[11px] font-bold text-[#3973bf]">
                        Her zaman açık
                      </span>
                    </div>
                    <p className="mt-1.5 text-[12.5px] leading-[1.65] text-[#5b6472]">{cat.description}</p>
                  </div>
                ))}

              {optionalCategories.map((cat) => {
                const checked = cat.in_use && selection.includes(cat.id);
                return (
                  <label
                    key={cat.id}
                    className={`flex items-start gap-3 rounded-[14px] border border-[#e6e9ef] p-4 ${
                      cat.in_use ? 'cursor-pointer hover:border-[#c7d3e6]' : 'opacity-70'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#3973bf]"
                      checked={checked}
                      disabled={!cat.in_use}
                      onChange={() => toggle(cat.id)}
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-[14px] font-semibold text-[#2b2b2b]">{cat.label}</span>
                        {!cat.in_use && (
                          <span className="rounded-full bg-[#f0f2f6] px-2.5 py-1 text-[11px] font-bold text-[#8a93a2]">
                            Kullanılmıyor
                          </span>
                        )}
                      </span>
                      <span className="mt-1.5 block text-[12.5px] leading-[1.65] text-[#5b6472]">
                        {cat.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => save(['zorunlu'], 'preferences')}
                className="rounded-[12px] border border-[#d5dbe5] px-4 py-2.5 text-[13px] font-semibold text-[#4a5260] transition-colors hover:bg-[#f4f6fa]"
              >
                Yalnızca Zorunlu
              </button>
              <button
                type="button"
                onClick={() => save(selection, 'preferences')}
                className="rounded-[12px] bg-[#3973bf] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Tercihlerimi Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
